import Link from 'next/link';
import Image from 'next/image';
import styles from './Footer.module.scss';
import classNames from 'classnames/bind';
import { useRouter } from 'next/router';
import { useQuery, gql } from '@apollo/client';

let cx = classNames.bind(styles);

// GraphQL query to fetch all office areas
const GET_ALL_AREAS = gql`
  query GetAllAreasForFooter($language: LanguageCodeFilterEnum!) {
    omrader(first: 100, where: { language: $language }) {
      nodes {
        id
        slug
        name
      }
    }
  }
`;

const translations = {
  nb: {
    contact: {
      phone: 'Ring oss på +47 994 26 996',
      email: 'Send e-post til post@spacefinder.no',
      address: 'Finn oss i Tjuvholmen allé 3, 0252 Oslo',
      license: 'Konsesjon fra Finanstilsynet'
    },
    sections: {
      usefulLinks: 'Nyttige linker',
      contactUs: 'Kontakt oss',
      officeAreas: 'Populære kontorområder'
    },
    urls: {
      availableOffices: '/kontorer',
      spacefinder: '/#spacefinder',
      businessAreas: '/naringsmegleren',
      marketInsight: '/kontoristen',
      contactUs: '/kontakt-oss',
      aboutUs: '/om-oss',
      privacy: '/personvern',
      partners: '/samarbeidspartnere',
      rentOut: '/lei-ut-kontor',
      faq: '/faq',
      sustainability: '/baerekraft'
    },
    usefulLinks: {
      availableOffices: 'Ledige kontorer',
      spacefinder: 'Spacefinder',
      businessAreas: 'Næringsmegler',
      marketInsight: 'Kontoristen',
      partners: 'Våre samarbeidspartnere',
      rentOut: 'Lei ut kontor',
      faq: 'FAQ',
      sustainability: 'Bærekraft'
    },
    contactLinks: {
      contactUs: 'Kontakt oss',
      aboutUs: 'Om oss',
      privacy: 'Personvern'
    },
    footer: {
      copyright: '© 2025 Spacefinder. All rights reserved. Løsningen er levert av'
    }
  },
  en: {
    contact: {
      phone: 'Call us at +47 994 26 996',
      email: 'Send email to post@spacefinder.no',
      address: 'Find us at Tjuvholmen allé 3, 0252 Oslo',
      license: 'Licensed by Finanstilsynet'
    },
    sections: {
      usefulLinks: 'Useful Links',
      contactUs: 'Contact Us',
      officeAreas: 'Popular office areas'
    },
    urls: {
      availableOffices: '/en/offices',
      spacefinder: '/en/#spacefinder',
      businessAreas: '/en/broker',
      marketInsight: '/en/insights',
      contactUs: '/en/contact-us',
      aboutUs: '/en/about-us',
      privacy: '/en/privacy',
      partners: '/en/partners',
      rentOut: '/en/rent-out-office-space',
      faq: '/en/faq',
      sustainability: '/en/sustainability'
    },
    usefulLinks: {
      availableOffices: 'Available Offices',
      spacefinder: 'Spacefinder',
      businessAreas: 'Broker',
      marketInsight: 'Insights',
      partners: 'Our Partners',
      rentOut: 'Rent Out Office',
      faq: 'FAQ',
      sustainability: 'Sustainability'
    },
    contactLinks: {
      contactUs: 'Contact Us',
      aboutUs: 'About Us',
      privacy: 'Privacy'
    },
    footer: {
      copyright: '© 2025 Spacefinder . All rights reserved. Solution delivered by'
    }
  }
};

export default function Footer({ logo }) {
  const router = useRouter();
  const t = translations[router.locale || 'nb'];
  const language = router.locale?.toUpperCase() === 'EN' ? 'EN' : 'NB';

  // Fetch all office areas
  const { data: areasData } = useQuery(GET_ALL_AREAS, {
    variables: { language },
    errorPolicy: 'ignore'
  });

  const areas = areasData?.omrader?.nodes || [];

  // Generate area URL based on locale
  const generateAreaUrl = (area) => {
    if (!area.slug && !area.name) return { pathname: '#', locale: false };
    
    let areaSlug = area.slug || area.name?.toLowerCase().replace(/\s+/g, '-');
    
    // Clean area slug based on locale (remove language suffixes)
    if (router.locale === 'nb') {
      if (areaSlug && areaSlug.endsWith('-nb')) {
        areaSlug = areaSlug.replace('-nb', '');
      }
      return { 
        pathname: `/kontorer/til-leie/${areaSlug}/`,
        locale: 'nb'
      };
    } else {
      if (areaSlug && areaSlug.endsWith('-en')) {
        areaSlug = areaSlug.replace('-en', '');
      }
      
      // Map Norwegian area names to English equivalents
      const areaMapping = {
        'sentrum': 'city-centre',
        'bjorvika': 'bjorvika',
        'skoyen': 'skoyen',
        'lysaker': 'lysaker',
        'majorstuen': 'majorstuen',
        'frogner': 'frogner',
        'vika-aker-brygge': 'vika-aker-brygge',
        'grunerlokka': 'grunerlokka',
        'solli-plass': 'solli-plass',
        'hasle-loren': 'hasle-loren',
        'helsfyr': 'helsfyr',
        'okern': 'okern',
        'bryn': 'bryn',
        'drammen': 'drammen',
        'asker': 'asker',
        'tonsberg': 'tonsberg',
        'nydalen': 'nydalen',
        'ski': 'ski',
        'alna': 'alna',
        'ensjo': 'ensjo',
        'fornebu': 'fornebu',
        'sandvika': 'sandvika',
        'tjuvholmen': 'tjuvholmen'
      };
      
      const mappedAreaSlug = areaMapping[areaSlug] || areaSlug;
      // Return href object with explicit locale for proper routing
      return {
        pathname: `/en/offices/for-rent/${mappedAreaSlug}/`,
        locale: false // Disable automatic locale handling since we include /en/ in pathname
      };
    }
  };

  return (
    <footer className={cx('footer')}>
      <div className={cx('footer-content')}>
        <div className={cx('footer-grid')}>
          <div className={cx('footer-column', 'company-info')}>
            {logo?.mediaItemUrl && (
              <div className={cx('footer-logo')}>
                <Image
                  src={logo.mediaItemUrl}
                  alt={logo.altText || 'Spacefinder'}
                  width={220}
                  height={60}
                  loading="lazy"
                  style={{ width: 'auto', height: 'auto' }}
                />
              </div>
            )}
            <h2>Spacefinder AS</h2>
            <p><a href="tel:+4799426996">{t.contact.phone}</a></p>
            <p><a href="mailto:post@spacefinder.no">{t.contact.email}</a></p>
            <p><a href="https://www.google.com/maps?q=Tjuvholmen+allé+3,+0252+Oslo" target="_blank" rel="noopener noreferrer">{t.contact.address}</a></p>
            <p><a href="https://www.finanstilsynet.no/virksomhetsregisteret/detalj/?id=241807" target="_blank" rel="noopener noreferrer">{t.contact.license}</a></p>
            <div className={cx('social-links')}>
              <Link href="https://no.linkedin.com/company/spacefinder-no">
                <span className={cx('linkedin')}>in</span>
              </Link>
            </div>
          </div>

          {/* Office Areas Section with Secondary Links below */}
          <div className={cx('footer-column', 'areas-column')}>
            {/* Office Areas */}
            {areas.length > 0 && (
              <div className={cx('areas-section')}>
                <h3>{t.sections.officeAreas}</h3>
                <div className={cx('areas-grid-inline')}>
                  {areas.map((area) => {
                    const urlObj = generateAreaUrl(area);
                    
                    return (
                      <Link
                        key={area.id}
                        href={urlObj.pathname}
                        locale={urlObj.locale}
                        className={cx('area-link')}
                        scroll={true}
                      >
                        {area.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Secondary Links in same column */}
            <div className={cx('secondary-links-stacked')}>
              <div className={cx('secondary-column')}>
                <h3>{t.sections.usefulLinks}</h3>
                <ul>
                  <li><Link href={t.urls.availableOffices}>{t.usefulLinks.availableOffices}</Link></li>
                  <li><Link href={t.urls.spacefinder}>{t.usefulLinks.spacefinder}</Link></li>
                  <li><Link href={t.urls.businessAreas}>{t.usefulLinks.businessAreas}</Link></li>
                  <li><Link href={t.urls.marketInsight}>{t.usefulLinks.marketInsight}</Link></li>
                  <li><Link href={t.urls.partners}>{t.usefulLinks.partners}</Link></li>
                  <li><Link href={t.urls.rentOut}>{t.usefulLinks.rentOut}</Link></li>
                  <li><Link href={t.urls.faq}>{t.usefulLinks.faq}</Link></li>
                  <li><Link href={t.urls.sustainability}>{t.usefulLinks.sustainability}</Link></li>
                </ul>
              </div>

              <div className={cx('secondary-column')}>
                <h3>{t.sections.contactUs}</h3>
                <ul>
                  <li><Link href={t.urls.contactUs}>{t.contactLinks.contactUs}</Link></li>
                  <li><Link href={t.urls.aboutUs}>{t.contactLinks.aboutUs}</Link></li>
                  <li><Link href={t.urls.privacy}>{t.contactLinks.privacy}</Link></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className={cx('footer-bottom')}>
          <p>{t.footer.copyright} <a 
            href="https://mediabooster.no"
            style={{
              textDecoration: 'underline',
              fontWeight: '500',
              color: 'var(--accent-color)'
            }}
          >
            Mediabooster
          </a></p>
        </div>
      </div>
    </footer>
  );
}
